// The phone detail sheet — DetailPanel's twin. Extracted from
// PurchasingQueuePage.js at Session 79.14c.
//
// CHANGE THIS AND DetailPanel TOGETHER, ALWAYS. They render the same facts in
// two layouts and nothing but discipline keeps them agreeing; that divergence is
// what #12, #13 and #215 each cost a session to collapse after the fact.
//
// Mobile is the priority surface, so this is the copy that matters most.

import React from 'react';
import { IconMail, IconPhone, IconTruck } from '@tabler/icons-react';
import { fmtDate } from '../../lib/utils';
import { ageDays, requestedSummary, BRANCHES } from '../../lib/purchasingQueue';
import { itemLabel, itemUom, trackingSummary, trackingLabel, preferredVendorPicks } from '../../lib/requestItems';
import { INVENTORY_BUCKET } from '../../lib/storedFile';
import { canCancelFromHere } from '../../lib/materialRequests';
import Overlay from '../Overlay';
import StoredImage from '../StoredImage';
import TrackingRow from '../inventory/TrackingRow';
import PurchaseOrderDownload from './PurchaseOrderDownload';
import ApprovedToOrderBanner from './ApprovedToOrderBanner';
import VendorOrderNumberRow from './VendorOrderNumberRow';
import OrderIdentifiers from '../OrderIdentifiers';

export default function MobileDetailSheet({ req, leadKey = null, onClose, openMarkOrdered, openTracking, openApproval, onCancelRequest, canContact, canAction, canApprovePurchase, canShortClose, onShortClose, onReopen, onSaveVendorOrderRef }) {
  if (!req) return null;
  const items = req.material_request_items || [];
  const item = items[0];
  const isOffCatalog = item?.is_off_catalog;
  const itemName = isOffCatalog ? (item.off_catalog_description || '—') : (item?.inventory_items?.name || '—');
  const isMultiItem = items.length > 1;
  const track = trackingSummary(items);
  const qty = item?.qty_requested ?? '—';
  const uom = itemUom(item);
  const branchName = BRANCHES.find(b => b.id === req.branch_id)?.name || '—';
  // #304. Kept identical to DetailPanel's — the two render the same facts in
  // two layouts and a condition that differs between them is how they drift.
  const isSettled = req.status === 'received' || req.status === 'closed';
  const isPending = req.status === 'approved';
  const vendorPicks = preferredVendorPicks(items);
  const preferred = isMultiItem ? null : (item?.inventory_items?.vendor_items?.find(vi => vi.is_preferred) || null);
  const preferredVendor = preferred?.inventory_vendors || null;

  const statusPill = {
    approved: req.approved_at
      ? { label: 'Ready to order', bg: '#E8F5E9', color: '#1B5E20' }
      : { label: 'Pending', bg: '#FAEEDA', color: '#633806' },
    ordered:  { label: 'Ordered', bg: '#E6F1FB', color: '#0C447C' },
    received: { label: 'Received', bg: '#E8F5E9', color: '#2E7D32' },
    pending_purchase_approval: { label: 'Needs approval', bg: '#FBEAF0', color: '#993556' },
  }[req.status] || { label: req.status, bg: '#F0F0F0', color: '#5C5C5C' };

  // #250 / #148 Split C, Session 79.14. Converted to Overlay.js - locked rule 3.
  //
  // DEPTH 3000 IS THE LOWEST OF THE FIVE AND THAT IS THE WHOLE REASON Overlay
  // NEEDED A zIndex PROP. This sheet is the base layer on a phone: every action
  // the buyer takes from it - Mark ordered, tracking, approval - opens ON TOP of
  // it at 4000, and the cancel confirmation opens above those at 5000. All five
  // render as siblings, so with one fixed depth the DOM order would decide, and
  // this sheet is rendered LAST - which would put it over everything it launches.
  return (
    <Overlay
      variant="sheet"
      zIndex={3000}
      onClose={onClose}
      panelStyle={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflowY: 'hidden', overflowX: 'hidden' }}
    >
      <>
        <div style={{ width: 36, height: 4, borderRadius: 2, background: '#D0D0D0', margin: '10px auto 0', flexShrink: 0 }} />
        <div style={{ overflowY: 'auto', padding: '14px 16px 40px' }}>
          <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: 4 }}>{branchName} branch</div>
          {/* #545, D13. Its desktop twin carries the identical block — the two
              render the same facts in two layouts and must change together. */}
          <OrderIdentifiers req={req} leadKey={leadKey} style={{ marginBottom: 8 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0F0F0F' }}>
              {itemName}
              {isMultiItem && <span style={{ fontWeight: 400, fontSize: 15, color: '#5C5C5C' }}> +{items.length - 1} more</span>}
            </div>
            {isOffCatalog && (
              <span style={{ fontSize: 11, fontWeight: 700, color: '#A32D2D', background: '#FFEBEE', border: '1px solid #FFCDD2', borderRadius: 6, padding: '2px 8px' }}>Not in catalog</span>
            )}
          </div>
          <span style={{ display: 'inline-block', background: statusPill.bg, color: statusPill.color, borderRadius: 5, padding: '2px 8px', fontSize: 11, fontWeight: 600, marginBottom: 14 }}>{statusPill.label}</span>

          {/* #410. Mobile is the priority surface, so this is the same control
              in the same place rather than a desktop-only affordance — Jennifer
              reads a vendor's confirmation email on her phone. */}
          <VendorOrderNumberRow req={req} canAction={canAction} onSave={onSaveVendorOrderRef} />

          {/* #413 — same control, same place as desktop, for the same reason
              #410's row gives one line above: Jennifer works this queue from her
              phone, and a document she can only retrieve at a desk is one she
              cannot retrieve while she is on the phone to the vendor. */}
          {req.status === 'ordered' && (
            <div style={{ marginBottom: 12 }}>
              <PurchaseOrderDownload poDocument={req.po_document} label="Download the PO sent" />
            </div>
          )}

          <ApprovedToOrderBanner req={req} compact />

          {isOffCatalog && item?.photo_url && (
            <StoredImage value={item.photo_url} bucket={INVENTORY_BUCKET} alt="Request" style={{ width: '100%', borderRadius: 8, marginBottom: 12, objectFit: 'cover', maxHeight: 140 }} />
          )}

          {isOffCatalog && (
            <div style={{ background: '#FFF8EC', border: '1px solid #EF9F27', borderLeft: '3px solid #EF9F27', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#9A5F00', textTransform: 'uppercase', marginBottom: 4 }}>Description</div>
              <div style={{ fontSize: 13, color: '#3C3C3C', lineHeight: 1.5 }}>{item.off_catalog_description}</div>
              {item.off_catalog_vendor_name && (
                <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 6 }}>Suggested: <strong>{item.off_catalog_vendor_name}</strong></div>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 3 }}>Requested by</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F0F' }}>{req.users?.full_name || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 3 }}>{isMultiItem ? 'Items' : 'Qty needed'}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0F0F0F' }}>{isMultiItem ? `${items.length} items` : `${qty} ${uom}`}</div>
            </div>
            {/* The Work order cell rendered only when present, so this grid
                changed shape between two orders. It is in the identifier block
                at the top of the sheet now — D15. */}
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 3 }}>Waiting</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: ageDays(req.created_at) >= 3 ? '#9A5F00' : '#0F0F0F' }}>{ageDays(req.created_at)} days</div>
            </div>
          </div>

          {req.notes && (
            <div style={{ background: '#FAEEDA', border: '1px solid #EF9F27', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#633806', marginBottom: 12 }}>
              {req.notes}
            </div>
          )}

          {!isOffCatalog && isMultiItem && (
            <div style={{ background: '#F8F8F8', border: '1px solid #E8E8E8', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 5 }}>Preferred vendors</div>
              {vendorPicks.length > 0 ? vendorPicks.map(p => (
                <div key={p.vendor.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, padding: '5px 0', borderBottom: '1px solid #EDEDED' }}>
                  <span style={{ fontWeight: 600, color: '#0F0F0F' }}>{p.vendor.name}</span>
                  <span style={{ color: '#7A7A7A', flexShrink: 0 }}>{p.count} of {items.length} items</span>
                </div>
              )) : (
                <div style={{ fontSize: 12, color: '#9A9A9A', fontStyle: 'italic' }}>No preferred vendor on file for any item</div>
              )}
              <div style={{ fontSize: 11, color: '#9A9A9A', marginTop: 6 }}>
                Per-item pricing and vendor contact are on the Mark ordered sheet.
              </div>
            </div>
          )}

          {!isOffCatalog && !isMultiItem && (
            <div style={{ background: '#F8F8F8', border: '1px solid #E8E8E8', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 5 }}>Preferred vendor</div>
              {preferredVendor ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0F0F0F', marginBottom: 2 }}>{preferredVendor.name}</div>
                  <div style={{ fontSize: 11, color: '#5C5C5C', marginBottom: 6 }}>
                    {preferred?.vendor_item_number
                      ? <>Item # <strong style={{ color: '#0F0F0F' }}>{preferred.vendor_item_number}</strong>{preferred?.unit_cost != null ? ` · $${parseFloat(preferred.unit_cost).toFixed(2)}/${uom || 'unit'}` : ''}</>
                      : <span style={{ color: '#B0B0B0', fontStyle: 'italic' }}>No item # on file{preferred?.unit_cost != null ? ` · $${parseFloat(preferred.unit_cost).toFixed(2)}/${uom || 'unit'}` : ''}</span>
                    }
                  </div>
                  {canContact && (
                    <>
                      {preferredVendor.contact_name && (
                        <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: 6 }}>Contact: {preferredVendor.contact_name}</div>
                      )}
                      <div style={{ display: 'flex', gap: 12 }}>
                        {preferredVendor.phone && (
                          <a href={`tel:${preferredVendor.phone}`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#1D9E75', fontWeight: 600, textDecoration: 'none' }}>
                            <IconPhone size={13} /> {preferredVendor.phone}
                          </a>
                        )}
                        {preferredVendor.email && (
                          <a href={`mailto:${preferredVendor.email}`} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#1D9E75', fontWeight: 600, textDecoration: 'none' }}>
                            <IconMail size={13} /> {preferredVendor.email}
                          </a>
                        )}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 12, color: '#9A9A9A', fontStyle: 'italic' }}>No preferred vendor on file</div>
              )}
            </div>
          )}

          {isMultiItem && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>Items on this request</div>
              {items.map(it => (
                <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, color: '#3C3C3C', padding: '5px 0', borderBottom: '1px solid #F5F5F3' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>{itemLabel(it)}</span>
                  <span style={{ flexShrink: 0, color: '#7A7A7A' }}>x{it.qty_requested} {itemUom(it)}</span>
                </div>
              ))}
            </div>
          )}

          {/* #304, Session 79.14d. See DetailPanel for the full reasoning: this
              condition read `status === 'ordered'` alone, which put the reopen
              control — the buyer's only way to reverse a short close — out of
              reach for exactly the settled orders this issue is about. BOTH
              SURFACES OR NEITHER, which is this file's own opening rule. */}
          {(req.status === 'ordered' || isSettled) && items.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.4px' }}>{isSettled ? 'Lines on this order' : 'Tracking'}</div>
                <div style={{ fontSize: 11, color: '#7A7A7A' }}>{isSettled ? 'Settled' : trackingLabel(track)}</div>
              </div>
              {items.map(it => (
                <TrackingRow
                  key={it.id}
                  it={it}
                  canAction={canAction && !isSettled}
                  onAddTracking={target => { onClose(); openTracking(req, target); }}
                  canShortClose={canShortClose}
                  onShortClose={target => { onClose(); onShortClose(req, target); }}
                  onReopen={onReopen}
                />
              ))}
              {/* Gate callout J, and it matters more on a phone: the sheet
                  closes over the list, so "where did it go" has even less on
                  screen to answer it. */}
              {isSettled && canShortClose && items.some(it => it.receipt_closed) && (
                <div style={{ fontSize: 11, color: '#7A7A7A', marginTop: 8, lineHeight: 1.45 }}>
                  Reopening a line puts this order back on <strong style={{ color: '#5C5C5C' }}>On order</strong>.
                </div>
              )}
            </div>
          )}

          <div style={{ fontSize: 10, fontWeight: 700, color: '#7A7A7A', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 4 }}>Request history</div>
          <div style={{ fontSize: 11, color: '#7A7A7A', marginBottom: 16 }}>
            {fmtDate(req.created_at)} · {req.users?.full_name || '—'} requested {requestedSummary(items)}
            {req.work_order_ref ? ` · ${req.work_order_ref}` : ''}
          </div>

          <div style={{ borderTop: '1px solid #E8E8E8', paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {isPending && canAction && (
              <button onClick={() => { onClose(); openMarkOrdered(req); }}
                style={{ width: '100%', padding: '11px 0', background: '#1D9E75', color: 'white', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Mark ordered
              </button>
            )}
            {req.status === 'ordered' && canAction && items.length === 1 && (
              <button onClick={() => { onClose(); openTracking(req, items[0]); }}
                style={{
                  width: '100%', padding: '11px 0',
                  background: track.tracked > 0 ? 'white' : '#0F0F0F',
                  color: track.tracked > 0 ? '#0F0F0F' : 'white',
                  border: track.tracked > 0 ? '1px solid #E0E0E0' : 'none',
                  borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                <IconTruck size={14} />
                {track.tracked > 0 ? 'Update tracking' : 'Add tracking'}
              </button>
            )}
            {/* #642 -- the same rule as the desk panel, from one place. */}
            {canCancelFromHere(req) && canAction && (
              <button onClick={() => { onClose(); onCancelRequest(req); }}
                style={{ width: '100%', padding: '11px 0', background: 'white', color: '#A32D2D', border: '1px solid #F09595', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Cancel request
              </button>
            )}
            {req.status === 'pending_purchase_approval' && canApprovePurchase && (
              <button onClick={() => { onClose(); openApproval(req); }}
                style={{ width: '100%', padding: '11px 0', background: '#993556', color: 'white', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Review approval
              </button>
            )}
          </div>
        </div>
      </>
    </Overlay>
  );
}

// Issue #19 — real cancel confirmation, replaces the three dead/misleading Cancel buttons.
// Writes status: 'cancelled' (distinct from 'denied' — a cancel happens after approval,
// a deny happens before it; kept as separate enum values for accurate analytics).
